// Script to capture initial traffic source and UTM parameters and store them in cookies
// for later use (e.g., during signup). This script only sets the cookies once per user
// (on their first visit), and it sets default values if no UTM parameters are present.

import { onPageLoad } from '@/js/modules/page_load';

onPageLoad(() => {
  function setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie =
      name + '=' + encodeURIComponent(value || '') + expires + '; path=/; domain=.designfiles.co';
  }

  function getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
    return null;
  }

  function getDfUtmCookies() {
    if (!getCookie('df_initialized_at')) {
      return '';
    } else {
      const cookieKeys = [
        'df_initial_referrer',
        'df_initial_referring_domain',
        'df_initial_landing_page_url',
        'df_initial_utm_source',
        'df_initial_utm_medium',
        'df_initial_utm_campaign',
        'df_initial_utm_term',
        'df_initial_utm_content',
      ];
      const result = {};
      cookieKeys.forEach((name) => {
        result[name] = getCookie(name) || '';
      });
      return JSON.stringify(result);
    }
  }

  // only set cookies if not already set
  if (!getCookie('df_initialized_at')) {
    // --- First touch UTM and Source detection ---
    const urlParams = new URLSearchParams(window.location.search);

    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');
    const utmCampaign = urlParams.get('utm_campaign');
    const utmTerm = urlParams.get('utm_term');
    const utmContent = urlParams.get('utm_content');

    // Figure out referrer and domain
    const ref = document.referrer || '';
    let refDomain = '';
    try {
      refDomain = ref ? new URL(ref).hostname : '';
    } catch {
      refDomain = '';
    }
    const sameDomain = refDomain && refDomain === window.location.hostname;

    // Classify a traffic source
    const organicHosts = ['google.', 'bing.', 'yahoo.', 'duckduckgo.', 'ecosia.', 'yandex.'];
    const isOrganic = refDomain && organicHosts.some((h) => refDomain.includes(h));
    let trafficSource = '$direct';
    if (!sameDomain && refDomain) {
      trafficSource = isOrganic ? 'organic' : 'referral';
    }

    // Initial referrer and landing page url values
    setCookie('df_initialized_at', String(Date.now()), 90);
    setCookie('df_initial_referrer', ref || '$direct', 90);
    setCookie('df_initial_referring_domain', refDomain || '$direct', 90);
    setCookie('df_initial_landing_page_url', window.location.href, 90);

    // If no UTMs on the first visit, set sensible defaults from source
    if (!utmSource && !utmMedium && !utmCampaign && !utmTerm && !utmContent) {
      if (trafficSource === '$direct') {
        setCookie('df_initial_utm_source', '$direct', 90);
        setCookie('df_initial_utm_medium', '$direct', 90);
        setCookie('df_initial_utm_campaign', 'none', 90);
        setCookie('df_initial_utm_term', 'none', 90);
        setCookie('df_initial_utm_content', 'none', 90);
      } else if (trafficSource === 'organic') {
        setCookie('df_initial_utm_source', refDomain || 'organic', 90);
        setCookie('df_initial_utm_medium', 'organic', 90);
        setCookie('df_initial_utm_campaign', 'none', 90);
        setCookie('df_initial_utm_term', 'none', 90);
        setCookie('df_initial_utm_content', 'none', 90);
      } else {
        setCookie('df_initial_utm_source', refDomain || 'referral', 90);
        setCookie('df_initial_utm_medium', 'referral', 90);
        setCookie('df_initial_utm_campaign', 'none', 90);
        setCookie('df_initial_utm_term', 'none', 90);
        setCookie('df_initial_utm_content', 'none', 90);
      }
    } else {
      // If any of the UTMs are present in URL, then use those and set missing ones to 'none'
      setCookie('df_initial_utm_source', utmSource || 'none', 90);
      setCookie('df_initial_utm_medium', utmMedium || 'none', 90);
      setCookie('df_initial_utm_campaign', utmCampaign || 'none', 90);
      setCookie('df_initial_utm_term', utmTerm || 'none', 90);
      setCookie('df_initial_utm_content', utmContent || 'none', 90);
    }
  }

  // Pass combined utms from cookies to a hidden field on signup page. The invited-designer
  // signup page shares the body id but has no such field.
  const utmSourceField = document.getElementById('df_utm_source');
  if (document.body.id === 'designer-register' && utmSourceField) {
    utmSourceField.value = getDfUtmCookies();
  }
});
